import React from 'react';
import { TableColumnType } from '../Table';

/** Stable identifier for a column (falls back to index when keys collide). */
export function getColumnKey<T>(
  column: TableColumnType<T>,
  index: number,
): string {
  return column.key != null ? String(column.key) : `col-${index}`;
}

/** Human readable label for a column, or `undefined` when it has no text title. */
export function getColumnLabel<T>(
  column: TableColumnType<T>,
): string | undefined {
  return typeof column.title === 'string' ? column.title : undefined;
}

/** A column can be sorted from the card controls when it exposes a comparator. */
export function isSortable<T>(column: TableColumnType<T>): boolean {
  return typeof column.sorter === 'function' && !!getColumnLabel(column);
}

/** A column can be filtered from the card controls when it exposes filter logic. */
export function isFilterable<T>(column: TableColumnType<T>): boolean {
  return typeof column.onFilter === 'function' && !!getColumnLabel(column);
}

/** Reads the raw cell value from a record using the column's `dataIndex`. */
export function getCellValue<T>(
  column: TableColumnType<T>,
  record: T,
): unknown {
  const dataIndex = column.dataIndex;
  if (dataIndex == null) return undefined;

  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce<any>(
      (acc, key) => (acc == null ? acc : acc[key as keyof typeof acc]),
      record,
    );
  }

  return (record as any)[dataIndex as keyof T];
}

/** Renders a cell exactly as the table would, reusing the column's `render`. */
export function renderCell<T>(
  column: TableColumnType<T>,
  record: T,
  index: number,
): React.ReactNode {
  const value = getCellValue(column, record);
  if (typeof column.render === 'function') {
    const rendered = column.render(value as any, record, index);
    return (rendered as any)?.children ?? rendered;
  }
  return value as React.ReactNode;
}
